import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { Lock, Unlock, Trash2, ArrowLeft } from "lucide-react";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { nodeLabel } from "@/components/research-hub/labels";

const EXTRACTOR_HUMAN = "human";

export default function ResearchKGNode() {
  const { nodeId } = useParams<{ nodeId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: canEdit } = useResearchCapability("author");

  const { data: node } = useQuery({
    queryKey: ["kg_node", nodeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_kg_nodes").select("*").eq("id", nodeId!).single();
      if (error) throw error;
      return data as any;
    },
    enabled: !!nodeId,
  });

  const { data: edges } = useQuery({
    queryKey: ["kg_node_edges", nodeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_kg_edges")
        .select("id, from_node_id, to_node_id, edge_type, confidence, extractor, locked, weight, evidence_source_id, evidence_source_ids, properties, valid_from, valid_to, asserted_at, support_count")
        .or(`from_node_id.eq.${nodeId},to_node_id.eq.${nodeId}`)
        .order("confidence", { ascending: false, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      return data as any[];
    },
    enabled: !!nodeId,
  });

  const otherIds = Array.from(new Set((edges ?? []).flatMap((e) => [e.from_node_id, e.to_node_id]).filter((i) => i !== nodeId)));
  const { data: others } = useQuery({
    queryKey: ["kg_node_others", nodeId, otherIds.join(",")],
    queryFn: async () => {
      if (!otherIds.length) return [] as any[];
      const { data, error } = await supabase
        .from("research_kg_nodes").select("id,node_type,label").in("id", otherIds);
      if (error) throw error;
      return data as any[];
    },
    enabled: otherIds.length > 0,
  });
  const otherMap = new Map<string, any>((others ?? []).map((n) => [n.id, n]));

  // properties.ext_evidence — chrome-extension-attached evidence entries (§4.2/§4.3 thesis_attachment
  // "kg_node" branch: appended by research_capture_suggestion_decide, {source_id, note, anchor, by, at}[])
  const extEvidence: any[] = Array.isArray(node?.properties?.ext_evidence) ? node.properties.ext_evidence : [];

  // Edges' plural evidence_source_ids array (membership, distinct from the singular evidence_source_id
  // already rendered per-edge above) — evidence attached to an edge via the suggestions inbox accept path.
  const edgeEvidenceSourceIds = Array.from(
    new Set((edges ?? []).flatMap((e) => (Array.isArray(e.evidence_source_ids) ? e.evidence_source_ids : [])).filter(Boolean))
  );

  const sourceIds = Array.from(
    new Set([
      ...(edges ?? []).map((e) => e.evidence_source_id).filter(Boolean),
      ...edgeEvidenceSourceIds,
      ...extEvidence.map((ev) => ev?.source_id).filter(Boolean),
    ])
  );
  const { data: sources } = useQuery({
    queryKey: ["kg_node_sources", nodeId, sourceIds.join(",")],
    queryFn: async () => {
      if (!sourceIds.length) return [] as any[];
      const { data, error } = await supabase
        .from("research_sources")
        .select("id, title, source_type, occurred_at, external_url")
        .in("id", sourceIds);
      if (error) throw error;
      return data as any[];
    },
    enabled: sourceIds.length > 0,
  });

  const { data: similar } = useQuery({
    queryKey: ["kg_node_similar", nodeId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("match_kg_nodes", { _node_id: nodeId!, _limit: 8 });
      if (error) throw error;
      return (data ?? []) as { id: string; node_type: string; label: string; similarity: number }[];
    },
    enabled: !!nodeId && !!node?.embedding_model,
  });

  // Add-edge form
  const [newTargetLabel, setNewTargetLabel] = useState("");
  const [newTargetType, setNewTargetType] = useState("organisation");
  const [newEdgeType, setNewEdgeType] = useState("mentions");
  const [newDirection, setNewDirection] = useState<"out" | "in">("out");
  const [adding, setAdding] = useState(false);

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["kg_node", nodeId] });
    qc.invalidateQueries({ queryKey: ["kg_node_edges", nodeId] });
  };

  const addEdge = async () => {
    if (!newTargetLabel.trim()) { toast.error("Target label required"); return; }
    setAdding(true);
    try {
      const { data: tid, error: nErr } = await supabase.rpc("research_kg_upsert_node", {
        _node_type: newTargetType, _label: newTargetLabel.trim(),
        _external_id: null, _external_table: null, _properties: {},
      });
      if (nErr) throw nErr;
      const from = newDirection === "out" ? nodeId! : (tid as string);
      const to = newDirection === "out" ? (tid as string) : nodeId!;
      const { error: eErr } = await (supabase.rpc as any)("research_kg_upsert_edge", {
        _from: from, _to: to, _edge_type: newEdgeType,
        _extractor: EXTRACTOR_HUMAN, _confidence: 1.0,
        _evidence_source_id: null, _properties: { added_by: "ui" },
        _weight: 1.0, _locked: true,
      });
      if (eErr) throw eErr;
      toast.success("Edge added (locked, human extractor)");
      setNewTargetLabel("");
      refreshAll();
    } catch (e: any) { toast.error(e.message); } finally { setAdding(false); }
  };

  const removeEdge = async (id: string) => {
    if (!confirm("Delete this edge?")) return;
    const { error } = await supabase.from("research_kg_edges").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Edge removed");
    refreshAll();
  };

  const toggleEdgeLock = async (id: string, locked: boolean) => {
    const { error } = await supabase.from("research_kg_edges").update({ locked: !locked }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    refreshAll();
  };

  const toggleNodeLock = async () => {
    if (!node) return;
    const { error } = await supabase.from("research_kg_nodes").update({ locked: !node.locked }).eq("id", node.id);
    if (error) { toast.error(error.message); return; }
    refreshAll();
  };

  if (!nodeId) return null;
  if (!node) return <p className="text-sm text-muted-foreground p-4">Loading…</p>;

  const sourceMap = new Map<string, any>((sources ?? []).map((s) => [s.id, s]));
  const grouped: Record<string, any[]> = {};
  for (const e of edges ?? []) {
    (grouped[e.edge_type] ??= []).push(e);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/partner/research/kg")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to KG
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-xl">{node.label}</CardTitle>
            <Badge variant="outline">{node.node_type}</Badge>
            {node.locked && <Badge variant="secondary"><Lock className="h-3 w-3 mr-1" />locked</Badge>}
            <div className="ml-auto flex items-center gap-2">
              <Label htmlFor="lock" className="text-xs">Lock node</Label>
              <Switch id="lock" checked={!!node.locked} onCheckedChange={toggleNodeLock} disabled={!canEdit} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-sm space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-xs uppercase text-muted-foreground">Node id</div><div className="font-mono text-xs break-all">{node.id}</div></div>
            {node.external_table && <div><div className="text-xs uppercase text-muted-foreground">External</div><div className="font-mono text-xs break-all">{node.external_table} / {node.external_id}</div></div>}
            {node.embedding_model && <div><div className="text-xs uppercase text-muted-foreground">Embedding</div><div className="font-mono text-xs">{node.embedding_model}</div></div>}
            <div><div className="text-xs uppercase text-muted-foreground">Last seen</div><div className="text-xs">{node.last_seen_at ? format(new Date(node.last_seen_at), "dd/MM/yyyy HH:mm") : format(new Date(node.updated_at), "dd/MM/yyyy HH:mm")}</div></div>
          </div>
          {node.aliases?.length > 0 && (
            <div>
              <div className="text-xs uppercase text-muted-foreground mb-1">Aliases</div>
              <div className="flex flex-wrap gap-1">{node.aliases.map((a: string) => <Badge key={a} variant="outline">{a}</Badge>)}</div>
            </div>
          )}
          <details>
            <summary className="text-xs uppercase text-muted-foreground cursor-pointer">Properties</summary>
            <pre className="text-xs bg-muted/40 p-3 rounded overflow-x-auto mt-2">{JSON.stringify(node.properties ?? {}, null, 2)}</pre>
          </details>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Edges ({edges?.length ?? 0})</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(grouped).length === 0 && <p className="text-xs text-muted-foreground">No edges.</p>}
          {Object.entries(grouped).map(([type, list]) => (
            <div key={type}>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{type} ({list.length})</div>
              <div className="space-y-1">
                {list.map((e) => {
                  const isFrom = e.from_node_id === nodeId;
                  const otherId = isFrom ? e.to_node_id : e.from_node_id;
                  const other = otherMap.get(otherId);
                  const src = e.evidence_source_id ? sourceMap.get(e.evidence_source_id) : null;
                  return (
                    <div key={e.id} className="text-xs border border-border rounded p-2 flex items-start gap-2">
                      <div className="flex-1">
                        <span className="text-muted-foreground">{isFrom ? "→" : "←"}</span>{" "}
                        {other ? (
                          <Link to={`/partner/research/kg/${other.id}`} className="font-medium hover:underline">
                            {other.label}
                          </Link>
                        ) : <span className="text-muted-foreground italic">{nodeLabel(null, otherId)}</span>}
                        {other && <span className="text-muted-foreground"> ({other.node_type})</span>}
                        <div className="flex flex-wrap gap-2 mt-1 text-muted-foreground">
                          {e.confidence != null && <span>conf {Number(e.confidence).toFixed(2)}</span>}
                          {e.weight != null && <span>w {Number(e.weight).toFixed(2)}</span>}
                          {e.support_count != null && e.support_count > 1 && <span>×{e.support_count}</span>}
                          {e.extractor && <span className="font-mono">{e.extractor}</span>}
                          {e.asserted_at && <span>{format(new Date(e.asserted_at), "dd/MM/yyyy")}</span>}
                          {src && <Link to={`/partner/research/items?source=${src.id}`} className="hover:underline">src: {src.title?.slice(0, 60) ?? src.id.slice(0, 8)}</Link>}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleEdgeLock(e.id, !!e.locked)} title={e.locked ? "Unlock" : "Lock"}>
                            {e.locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeEdge(e.id)} title="Delete">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {canEdit && (
        <Card>
          <CardHeader><CardTitle className="text-base">Add edge (human, locked)</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <div className="grid gap-1">
              <Label className="text-xs">Direction</Label>
              <select className="h-10 px-2 rounded-md border border-input bg-background text-sm" value={newDirection} onChange={(e) => setNewDirection(e.target.value as any)}>
                <option value="out">this → target</option>
                <option value="in">target → this</option>
              </select>
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">Edge type</Label>
              <select className="h-10 px-2 rounded-md border border-input bg-background text-sm" value={newEdgeType} onChange={(e) => setNewEdgeType(e.target.value)}>
                {["mentions","manages","holds","works_at","covers","aligns_with","conflicts_with","tracks","parent_of","child_of","share_class_of","flags_risk","recommends_buy","recommends_sell","authored","tagged_with","cited_in"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">Target node_type</Label>
              <select className="h-10 px-2 rounded-md border border-input bg-background text-sm" value={newTargetType} onChange={(e) => setNewTargetType(e.target.value)}>
                {["organisation","fund","person","contact","sector","theme","topic","region","event","regulation","portfolio","research_item"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid gap-1 md:col-span-2">
              <Label className="text-xs">Target label (creates or matches)</Label>
              <Input value={newTargetLabel} onChange={(e) => setNewTargetLabel(e.target.value)} placeholder="e.g. Artemis" maxLength={300} />
            </div>
            <Button onClick={addEdge} disabled={adding} className="md:col-span-5 w-full md:w-auto">{adding ? "Adding…" : "Add edge"}</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Similar nodes</CardTitle></CardHeader>
        <CardContent>
          {!node.embedding_model && <p className="text-xs text-muted-foreground">Node not yet embedded — runs every 30 minutes.</p>}
          {node.embedding_model && (!similar || similar.length === 0) && <p className="text-xs text-muted-foreground">No similar nodes found.</p>}
          {similar && similar.length > 0 && (
            <div className="space-y-1">
              {similar.map((s) => (
                <Link key={s.id} to={`/partner/research/kg/${s.id}`} className="text-xs flex items-center gap-2 border border-border rounded p-2 hover:border-primary">
                  <span className="font-medium flex-1">{s.label}</span>
                  <Badge variant="outline">{s.node_type}</Badge>
                  <span className="text-muted-foreground">{(s.similarity * 100).toFixed(1)}%</span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Related sources ({sources?.length ?? 0})</CardTitle></CardHeader>
        <CardContent>
          {(!sources || sources.length === 0) && <p className="text-xs text-muted-foreground">No evidence sources linked yet.</p>}
          <div className="space-y-1">
            {sources?.map((s) => (
              <div key={s.id} className="text-xs border border-border rounded p-2">
                <div className="font-medium flex items-center gap-2">
                  {s.title ?? s.id}
                  {edgeEvidenceSourceIds.includes(s.id) && <Badge variant="outline">edge evidence</Badge>}
                </div>
                <div className="text-muted-foreground flex gap-2 mt-1">
                  <Badge variant="outline">{s.source_type}</Badge>
                  {s.occurred_at && <span>{format(new Date(s.occurred_at), "dd/MM/yyyy")}</span>}
                  {s.external_url && <a href={s.external_url} target="_blank" rel="noreferrer" className="hover:underline">open ↗</a>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {extEvidence.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Attached evidence ({extEvidence.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {extEvidence.map((ev: any, i: number) => {
              const src = ev?.source_id ? sourceMap.get(ev.source_id) : null;
              const quote = ev?.anchor?.quote;
              return (
                <div key={i} className="text-xs border border-border rounded p-2 space-y-1">
                  {quote && (
                    <blockquote className="border-l-2 border-primary/50 pl-2 italic text-foreground">
                      "{quote}"
                    </blockquote>
                  )}
                  {ev?.note && <div className="text-muted-foreground">{ev.note}</div>}
                  <div className="text-muted-foreground flex flex-wrap items-center gap-2">
                    {src ? (
                      <Link to={`/partner/research/items?source=${src.id}`} className="hover:underline">
                        {src.title?.slice(0, 60) ?? src.id.slice(0, 8)}
                      </Link>
                    ) : ev?.source_id ? (
                      <span className="font-mono">{String(ev.source_id).slice(0, 8)}</span>
                    ) : null}
                    {ev?.at && <span>{format(new Date(ev.at), "dd/MM/yyyy HH:mm")}</span>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
